/**
 * Auth-scoped Workbox cache helpers.
 *
 * The PWA service worker keeps two runtime cache buckets that hold
 * per-partner / per-user payloads (see `vite.config.ts` runtime
 * caching rules):
 *
 *   • `fz-api-mock`      — `/api/v1/mock/*` (NetworkFirst, 24h)
 *   • `fz-api-dashboard` — `/api/v1/dashboard/*` (NetworkFirst, 6h)
 *
 * On any auth boundary (logout, fresh login, dead-session detector)
 * we wipe these buckets so the next session can never see the
 * previous user's bytes from cache. The static-asset bucket
 * (`fz-static`) and the public status bucket (`fz-system-status`)
 * are intentionally left alone — they contain no per-user data.
 *
 * `Promise.allSettled` keeps the helper resilient: a single
 * `caches.delete()` rejection (Safari private mode quirk, partial
 * eviction, etc.) must not stop the auth flow.
 *
 * Returns a void promise so callers can `await` before redirecting,
 * but a failure here is never propagated — the auth boundary is the
 * source of truth, not the cache.
 */
const AUTH_SCOPED_CACHES = ['fz-api-mock', 'fz-api-dashboard'] as const

export async function clearAuthScopedCaches(): Promise<void> {
  if (typeof window === 'undefined' || !('caches' in window)) return
  await Promise.allSettled(AUTH_SCOPED_CACHES.map((name) => window.caches.delete(name)))
}
